"use client";
import React, { useMemo, useRef, useState } from "react";
import {
  Search,
  Filter as FilterIcon,
  Download,
  X,
  ChevronDown,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  MapPin,
  Phone,
  Mail,
  Calendar,
  User as UserIcon,
  GitBranch,
  Users,
} from "lucide-react";
import { useOrganizationChart } from "../../context/OrganizationChartContext";
import { CDN_URL } from "../../config/apiConfig";

const STATUS_COLORS = {
  active: "bg-blue-100 text-blue-600",
  inactive: "bg-red-100 text-red-600",
  pending: "bg-yellow-100 text-yellow-600",
};

const ROLE_PILL = "bg-green-100 text-green-600";
const GRADIENT = "linear-gradient(135deg, #2563eb, #d12465)";

/* ============================================================
   EmployeeCard and TreeNode are declared OUTSIDE the main
   component on purpose. If they were declared inside
   OrganizationChart(), React would treat them as brand-new
   component types on every re-render, forcing unnecessary
   remounts. Keeping them as stable, top-level components lets
   React reconcile by type/key instead.
============================================================ */

function EmployeeCard({ emp, big, ctx }) {
  const { root, selectedId, search, matchesSearch, matchesStatus, onSelect } = ctx;

  const isSelected = selectedId === emp.id;
  const dimmedBySearch = search && !matchesSearch(emp);
  const dimmedByStatus = !matchesStatus(emp);

  return (
    <div
      onClick={() => onSelect(emp.id)}
      className={[
        "bg-white rounded-lg border shadow-sm px-2.5 sm:px-3 py-2 flex items-center gap-2 cursor-pointer select-none transition-all",
        "hover:shadow-md",
        big ? "w-48 sm:w-56" : "w-44 sm:w-52",
        isSelected ? "ring-2 ring-emerald-400 border-emerald-300" : "border-gray-200",
        dimmedBySearch || dimmedByStatus ? "opacity-40" : "opacity-100",
      ].join(" ")}
      title={emp.name}
    >
      <img
        src={emp.avatar ? `${CDN_URL}/${emp.avatar}` : "/images/default-avatar.png"}
        alt={emp.name}
        className="w-9 h-9 rounded-full object-cover shrink-0"
      />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{emp.name}</p>
        <span className={`inline-block mt-0.5 text-[10px] font-medium px-2 py-0.5 rounded-full ${ROLE_PILL}`}>
          {emp.department} {emp.jobTitle.toLowerCase().includes("manager") || emp.department === "Executive" ? "/Manager" : ""}
        </span>
        <p className="text-[10px] text-gray-400 truncate flex items-center gap-1 mt-0.5">
          <MapPin className="w-2.5 h-2.5 shrink-0" />
          {emp.location}
        </p>
      </div>
    </div>
  );
}

function TreeNode({ emp, ctx }) {
  const { childrenOf, deptFilter, collapsed, toggleCollapsed } = ctx;
  const kids = (childrenOf.get(emp.id) || []).filter(
    (k) => deptFilter === "All" || k.department === deptFilter
  );
  const isCollapsed = collapsed.has(emp.id);

  return (
    <div className="flex flex-col items-start">
      <div className="flex items-center gap-1">
        {kids.length > 0 && (
          <button
            onClick={() => toggleCollapsed(emp.id)}
            className="p-0.5 rounded hover:bg-gray-100 text-gray-500 shrink-0"
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
        <EmployeeCard emp={emp} ctx={ctx} />
        {kids.length > 0 && (
          <span className="text-[10px] text-gray-400 ml-1 flex items-center gap-0.5 shrink-0">
            <Users className="w-3 h-3" />
            {kids.length}
          </span>
        )}
      </div>

      {kids.length > 0 && !isCollapsed && (
        <div className="mt-3 pl-4 sm:pl-6 ml-4 border-l-2 border-gray-200 space-y-3">
          {kids.map((child) => (
            <div key={child.id} className="relative">
              <span className="absolute -left-4 sm:-left-6 top-4 w-4 sm:w-6 h-px bg-gray-200" />
              <TreeNode emp={child} ctx={ctx} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrganizationChart() {
  const {
    employees,
    loading,
    error,
    setEmployees,
    fetchOrganizationChart,
  } = useOrganizationChart();

  const [deptFilter, setDeptFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  const [zoom, setZoom] = useState(1);
  const [collapsed, setCollapsed] = useState(() => new Set()); // ids whose children are hidden

  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState("details");

  const chartScrollRef = useRef(null);

  /* ---------------- derived lookups ---------------- */

  const byId = useMemo(() => {
    const m = new Map();
    employees.forEach((e) => m.set(e.id, e));
    return m;
  }, [employees]);

  const childrenOf = useMemo(() => {
    const m = new Map();
    employees.forEach((e) => {
      if (e.managerId != null) {
        if (!m.has(e.managerId)) m.set(e.managerId, []);
        m.get(e.managerId).push(e);
      }
    });
    return m;
  }, [employees]);

  const root = useMemo(() => {
    if (!employees.length) return null;

    const ids = new Set(employees.map((e) => e.id));

    return (
      employees.find(
        (e) => e.managerId == null || !ids.has(e.managerId)
      ) || null
    );
  }, [employees]);

  const departments = useMemo(() => {
    const list = Array.from(new Set(employees.map((e) => e.department))).filter(
      (d) => d !== root?.department
    );
    return list;
  }, [employees, root]);

  // Direct reports of root, grouped by department (these become the "columns")
  const deptManagers = useMemo(() => {
    if (!root) return [];

    const reports = childrenOf.get(root.id) || [];

    const grouped = new Map();

    reports.forEach((emp) => {
      if (!grouped.has(emp.department)) {
        grouped.set(emp.department, []);
      }
      grouped.get(emp.department).push(emp);
    });

    return Array.from(grouped.entries()).map(([dept, managers]) => ({
      dept,
      managers,
    }));
  }, [root, childrenOf]);

  const matchesSearch = (emp) => {
    const term = search.trim().toLowerCase();

    if (!term) return true;

    return (
      emp.name?.toLowerCase().includes(term) ||
      emp.designation?.toLowerCase().includes(term) ||
      emp.department?.toLowerCase().includes(term) ||
      emp.location?.toLowerCase().includes(term) ||
      emp.email?.toLowerCase().includes(term)
    );
  };

  const matchesStatus = (emp) => statusFilter === "All" || emp.status === statusFilter;

  /* ---------------- actions ---------------- */

  const toggleCollapsed = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const showChildren = () => {
    setCollapsed(new Set());
  };

  const showParent = () => {
    const collapsedIds = new Set();

    employees.forEach((emp) => {
      if (childrenOf.has(emp.id)) {
        collapsedIds.add(emp.id);
      }
    });

    setCollapsed(collapsedIds);
  };

  const exportCSV = () => {
    const visible = employees.filter(
      (e) => (deptFilter === "All" || e.department === deptFilter) && matchesStatus(e) && matchesSearch(e)
    );
    const header = ["Name", "Job Title", "Department", "Manager", "Location", "Start Date", "Mobile", "Email", "Status"];
    const rows = visible.map((e) => [
      e.name,
      e.jobTitle,
      e.department,
      byId.get(e.managerId)?.name || "",
      e.location,
      e.startDate || e.dateOfJoining || e.joiningDate || "",
      e.mobile,
      e.email,
      e.status,
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "organization-chart.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const selected = selectedId ? byId.get(selectedId) : null;

  // Bundled once per render and handed down to the stable, top-level
  // EmployeeCard / TreeNode components as a single prop.
  const ctx = {
    root,
    childrenOf,
    deptFilter,
    collapsed,
    toggleCollapsed,
    selectedId,
    search,
    matchesSearch,
    matchesStatus,
    onSelect: (id) => {
      setSelectedId(id);
      setActiveTab("details");
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-500">Loading organization chart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (!root) {
    return <div className="p-6 text-gray-500">No organization data found.</div>;
  }

  return (
    <div className="bg-sky-50">
      <div className="px-4 sm:px-6 md:px-9 py-5">
        <div className="flex items-center gap-2 mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Organization Chart</h1>
            <p className="text-xs sm:text-sm text-gray-400">
              {employees.length} Employees | {employees.filter((e) => e.status === "active").length} Active
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between mb-4 bg-white rounded-xl p-3 shadow-sm gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full xl:w-auto flex-wrap">
            {/* Department filter */}
            <select
              className="px-3 py-2 border rounded-md text-sm w-full sm:w-auto"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <option value="All">All Dept.</option>
              {departments.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>

            {/* Zoom */}
            <div className="flex items-center gap-2 border rounded-md px-2 py-2 w-full sm:w-auto">
              <button onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))} className="text-gray-500 hover:text-gray-800">
                <ZoomOut className="w-4 h-4" />
              </button>
              <input
                type="range"
                min="0.5"
                max="1.5"
                step="0.05"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full sm:w-24 md:w-32 accent-[#2563eb]"
              />
              <button onClick={() => setZoom((z) => Math.min(1.5, +(z + 0.1).toFixed(2)))} className="text-gray-500 hover:text-gray-800">
                <ZoomIn className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-400 w-9 shrink-0">{Math.round(zoom * 100)}%</span>
            </div>

            {/* Show parent / children */}
            <button
              onClick={showParent}
              className="px-3 py-2 border rounded-md text-sm text-gray-600 hover:bg-gray-50 w-full sm:w-auto whitespace-nowrap"
            >
              Show Parent
            </button>
            <button
              onClick={showChildren}
              className="px-3 py-2 border rounded-md text-sm text-gray-600 hover:bg-gray-50 w-full sm:w-auto whitespace-nowrap"
            >
              Show Children
            </button>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full xl:w-auto">
            <div className="relative w-full sm:w-auto">
              <button
                onClick={() => setShowFilterPanel((s) => !s)}
                className="flex items-center gap-2 px-4 py-2 rounded-md border text-sm text-gray-700 hover:bg-gray-50 w-full sm:w-auto justify-center"
              >
                <FilterIcon className="w-4 h-4" />
                Filter
              </button>

              {showFilterPanel && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowFilterPanel(false)}
                  />
                  <div className="absolute right-0 mt-2 w-72 max-w-[90vw] bg-white border rounded-lg shadow-lg p-3 z-50">
                    <label className="text-xs font-medium text-gray-600">Search</label>
                    <div className="relative mt-1 mb-3">
                      <Search className="absolute left-2 top-2.5 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Name or job title"
                        className="pl-8 pr-2 py-2 w-full border rounded-md text-sm outline-none"
                      />
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <button
                        onClick={() => {
                          setSearch("");
                          setStatusFilter("All");
                        }}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        Clear filters
                      </button>
                      <button
                        onClick={() => setShowFilterPanel(false)}
                        className="text-xs font-medium text-[#2563eb] hover:underline"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={exportCSV}
              className="flex items-center gap-2 text-white px-4 py-2 rounded-md text-sm w-full sm:w-auto justify-center shrink-0"
              style={{ background: GRADIENT }}
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Chart + side panel */}
        <div className="flex flex-col lg:flex-row gap-4 items-start">
          {/* Chart */}
          <div
            ref={chartScrollRef}
            className="bg-white shadow rounded-xl p-4 sm:p-6 overflow-auto w-full lg:flex-1 min-w-0 max-h-[60vh] sm:max-h-[65vh] lg:max-h-[calc(100vh-260px)]"
          >
            <div
              className="inline-flex flex-col items-center gap-6 min-w-full"
              style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}
            >
              {/* Root */}
              <EmployeeCard emp={root} big ctx={ctx} />
              <div className="w-px h-6 bg-gray-300" />

              {/* Department columns */}
              <div className="flex flex-wrap justify-center gap-6 sm:gap-10">
                {deptManagers
                  .filter(({ dept }) => deptFilter === "All" || dept === deptFilter)
                  .map(({ dept, managers }) => (
                    <div key={dept} className="flex flex-col items-center">
                      <div className="bg-slate-800 text-white text-sm font-medium rounded-md px-5 py-2 mb-3">
                        {dept}
                      </div>

                      <div className="w-px h-6 bg-gray-300 mb-3" />

                      <div className="space-y-4">
                        {managers.map((manager) => (
                          <TreeNode key={manager.id} emp={manager} ctx={ctx} />
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Backdrop for mobile bottom sheet */}
          {selected && (
            <div
              className="fixed inset-0 bg-black/40 z-30 lg:hidden"
              onClick={() => setSelectedId(null)}
            />
          )}

          {/* Side panel */}
          {selected && (
            <div className="fixed inset-x-0 bottom-0 z-40 max-h-[85vh] rounded-t-2xl lg:static lg:rounded-xl lg:max-h-none lg:w-[340px] lg:shrink-0 bg-white shadow-lg lg:shadow-sm border overflow-y-auto">
              <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <span className="text-xs font-medium text-gray-400">Employee Profile</span>
                <button onClick={() => setSelectedId(null)} className="text-gray-500 hover:text-gray-800">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-4 pb-4">
                <div className="flex flex-col items-center text-center border-b pb-4">
                  <img
                    src={selected.avatar ? `${CDN_URL}/${selected.avatar}` : "/images/default-avatar.png"}
                    alt={selected.name}
                    className="w-16 h-16 rounded-full object-cover mb-2"
                  />
                  <h3 className="text-lg font-semibold text-gray-800">{selected.name}</h3>
                  <p className="text-sm text-gray-400">
                    {selected.jobTitle} / {selected.department} Department
                  </p>
                  <button
                    className="mt-3 flex items-center gap-1 text-white text-sm px-4 py-2 rounded-md"
                    style={{ background: GRADIENT }}
                  >
                    View Profile <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-4 border-b mt-3">
                  {["details", "history"].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`pb-2 text-sm font-medium capitalize ${
                        activeTab === tab
                          ? "text-[#2563eb] border-b-2 border-[#2563eb]"
                          : "text-gray-400"
                      }`}
                    >
                      {tab === "details" ? "Details" : "Job History"}
                    </button>
                  ))}
                </div>

                {activeTab === "details" ? (
                  <div className="mt-4 space-y-3 text-sm">
                    <DetailRow icon={<UserIcon className="w-4 h-4" />} label="Name" value={selected.name} />
                    <DetailRow icon={<GitBranch className="w-4 h-4" />} label="Job Title" value={selected.jobTitle} />
                    <DetailRow
                      icon={<Users className="w-4 h-4" />}
                      label="Manager"
                      value={selected.managerName || byId.get(selected.managerId)?.name || "—"}
                      valueClass="text-[#2563eb]"
                    />
                    <DetailRow icon={<MapPin className="w-4 h-4" />} label="Location" value={selected.location} />
                    <DetailRow icon={<Calendar className="w-4 h-4" />} label="Start Date" value={selected.startDate} />
                    <DetailRow icon={<Phone className="w-4 h-4" />} label="Mobile" value={selected.mobile} valueClass="text-[#2563eb]" />
                    <DetailRow icon={<Mail className="w-4 h-4" />} label="Work Email" value={selected.email} valueClass="text-[#2563eb]" />
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-xs font-medium text-gray-500">Status</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selected.status] || "bg-gray-100 text-gray-600"}`}>
                        {selected.status}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    {(selected.jobHistory || []).length === 0 && (
                      <p className="text-sm text-gray-400">No job history recorded.</p>
                    )}
                    {(selected.jobHistory || []).map((h, i) => (
                      <div key={i} className="border rounded-md px-3 py-2">
                        <p className="text-sm font-medium text-gray-800">{h.title}</p>
                        <p className="text-xs text-gray-400">
                          {h.department} · {h.period}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon, label, value, valueClass = "text-gray-700" }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-gray-400 mt-0.5">{icon}</span>
      <div>
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className={`text-sm ${valueClass}`}>{value || "—"}</p>
      </div>
    </div>
  );
}